import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Trophy, TrendingUp, Target, Users, MessageSquare, BarChart3, History, Calendar, Settings, Flame, Film, User, Bell, Brain, Plus, BookOpen, Award, Swords, FileDown, Share2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { format } from 'date-fns';
import { PieChart, Pie, Cell, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import AddMatchDialog from '../components/AddMatchDialog';
import FriendsList from '../components/FriendsList';
import MessagingPanel from '../components/MessagingPanel';
import MatchHistory from '../components/MatchHistory';
import AccountSettings from '../components/AccountSettings';
import MobileBottomNav from '../components/MobileBottomNav';
import ShortsAndPosts from '../components/ShortsAndPosts';
import ProfilePage from '../components/ProfilePage';
import NotificationsPanel from '../components/NotificationsPanel';
import AICoach from '../components/AICoach';
import DiaryPage from '../components/DiaryPage';
import Achievements from '../components/Achievements';
import HeadToHead from '../components/HeadToHead';
import WeeklyReport from '../components/WeeklyReport';
import TournamentBracket from '../components/TournamentBracket';
import EmbedStatCard from '../components/EmbedStatCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#10b981', '#ef4444'];

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

const DashboardPage = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [matches, setMatches] = useState([]);
  const [topPlayers, setTopPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(() => searchParams.get('section') || 'stats');
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [viewingUserId, setViewingUserId] = useState(null);
  const [friendsList, setFriendsList] = useState([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchStats();
    fetchMatches();
    fetchTopPlayers();
    fetchUnreadCount();
    fetchFriendsList();
  }, []);

  useEffect(() => {
    if (user?.theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [user?.theme]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/matches/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatches(response.data);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTopPlayers = async () => {
    try {
      const response = await axios.get(`${API}/matches/global-stats`);
      setTopPlayers(response.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch top players:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadNotifs(response.data.count);
    } catch (error) {
      console.error('Failed to fetch notif count:', error);
    }
  };

  const fetchFriendsList = async () => {
    try {
      const response = await axios.get(`${API}/friends/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFriendsList(response.data);
    } catch (error) {
      console.error('Failed to fetch friends:', error);
    }
  };

  const handleMatchAdded = () => {
    // Stagger fetches to avoid blocking UI
    fetchStats();
    setTimeout(fetchMatches, 200);
    setTimeout(fetchTopPlayers, 400);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast({ title: 'Logged out', description: "You've been successfully logged out." });
  };

  const menuItems = [
    { id: 'stats', icon: BarChart3, label: 'Home' },
    { id: 'shorts', icon: Film, label: 'Shorts' },
    { id: 'history', icon: History, label: 'History' },
    { id: 'diary', icon: BookOpen, label: 'Diary' },
    { id: 'achievements', icon: Award, label: 'Badges' },
    { id: 'weekly-report', icon: TrendingUp, label: 'Weekly' },
    { id: 'head-to-head', icon: Swords, label: 'H2H' },
    { id: 'tournament', icon: Trophy, label: 'Tournaments' },
    { id: 'notifications', icon: Bell, label: 'Alerts', badge: unreadNotifs },
    { id: 'settings', icon: Settings, label: 'Settings' }
  ];

  const isDark = user?.theme === 'dark';

  // Pie chart data
  const pieData = stats ? [
    { name: 'Wins', value: stats.wins || 0 },
    { name: 'Losses', value: stats.losses || 0 }
  ].filter(d => d.value > 0) : [];

  // Performance data
  const recentCount = isMobile ? 8 : 10;
  const last10Matches = matches.slice(0, recentCount).reverse();
  const performanceData = last10Matches.map((match) => ({
    name: format(new Date(match.match_date), 'MM/dd'),
    score: match.user_score,
    opponent: match.opponent_score,
    result: match.result === 'win' ? 1 : 0
  }));

  // Cumulative wins chart data
  const sortedByDate = [...matches].reverse();
  let cumWins = 0;
  let cumLosses = 0;
  const winsOverTime = sortedByDate.map((m) => {
    if (m.result === 'win') cumWins++;
    else cumLosses++;
    return {
      name: format(new Date(m.match_date), 'MM/dd'),
      wins: cumWins,
      losses: cumLosses
    };
  });
  // Take last 15 data points to keep chart readable
  const winsChartData = winsOverTime.slice(-15);

  // Today's matches
  const today = new Date().toDateString();
  const todaysMatches = matches.filter(m => new Date(m.match_date).toDateString() === today);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-slate-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const renderHomeContent = () => (
    <div className="h-full flex flex-col lg:flex-row gap-4 p-4 pb-20 md:pb-4">
      {/* Left Column */}
      <div className="flex-1 flex flex-col gap-4">
        {/* Action Buttons - Log Match & AI Coach */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setActiveSection('addMatch')}
            className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
            data-testid="home-log-match-btn"
          >
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
              <Plus className="h-5 w-5" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-sm">Log Match</p>
              <p className="text-[10px] text-blue-100">Record a game</p>
            </div>
          </button>
          <button
            onClick={() => setActiveSection('ai-coach')}
            className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-500 text-white shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
            data-testid="home-ai-coach-btn"
          >
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
              <Brain className="h-5 w-5" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-sm">AI Coach</p>
              <p className="text-[10px] text-purple-100">Analyze & improve</p>
            </div>
          </button>
        </div>

        {/* Daily Login Streak */}
        {(user?.login_streak > 0) && (
          <div className={`flex items-center gap-3 p-3 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200'}`} data-testid="daily-streak">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <Flame className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.login_streak} day streak</p>
              <p className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-amber-700'}`}>Best: {user.max_login_streak || user.login_streak} days</p>
            </div>
            <div className="flex gap-0.5">
              {Array.from({ length: Math.min(user.login_streak, 7) }).map((_, i) => (
                <div key={i} className="w-2 h-5 rounded-sm bg-amber-400" />
              ))}
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''} data-testid="stat-matches">
            <CardContent className="p-3 text-center">
              <Trophy className="h-5 w-5 mx-auto text-yellow-500 mb-1" />
              <p className="text-2xl font-bold text-blue-600">{stats?.total_matches || 0}</p>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Matches</p>
            </CardContent>
          </Card>
          <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''} data-testid="stat-wins">
            <CardContent className="p-3 text-center">
              <TrendingUp className="h-5 w-5 mx-auto text-green-500 mb-1" />
              <p className="text-2xl font-bold text-green-600">{stats?.wins || 0}</p>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Wins</p>
            </CardContent>
          </Card>
          <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''} data-testid="stat-winrate">
            <CardContent className="p-3 text-center">
              <Target className="h-5 w-5 mx-auto text-blue-500 mb-1" />
              <p className="text-2xl font-bold text-blue-600">{stats?.win_rate || 0}%</p>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Win Rate</p>
            </CardContent>
          </Card>
          <Card className={`${stats?.streak_type === 'win' ? 'bg-green-50 border-green-300' : stats?.streak_type === 'loss' ? 'bg-red-50 border-red-300' : ''} ${isDark && !stats?.streak_type ? 'bg-slate-800 border-slate-700' : ''}`} data-testid="stat-streak">
            <CardContent className="p-3 text-center">
              <Flame className={`h-5 w-5 mx-auto mb-1 ${stats?.streak_type === 'win' ? 'text-orange-500' : 'text-blue-400'}`} />
              <p className="text-2xl font-bold">{stats?.current_streak || 0}</p>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Streak</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Win/Loss Pie */}
          <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''} data-testid="pie-chart-card">
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className={`text-sm font-medium ${isDark ? 'text-white' : ''}`}>Win/Loss Ratio</CardTitle>
            </CardHeader>
            <CardContent className="h-52 flex flex-col items-center justify-center px-4 pb-3">
              {pieData.length > 0 ? (
                <>
                  <div className="flex-1 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={35} outerRadius={60} dataKey="value" labelLine={false} strokeWidth={2}>
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [`${value}`, name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex items-center justify-center gap-4">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                      <span className="text-xs font-semibold text-emerald-600">Wins: {stats?.wins || 0}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                      <span className="text-xs font-semibold text-red-500">Losses: {stats?.losses || 0}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500 text-sm">No matches yet</div>
              )}
            </CardContent>
          </Card>

          {/* Performance Trend */}
          <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''} data-testid="performance-chart-card">
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className={`text-sm font-medium ${isDark ? 'text-white' : ''}`}>Performance Trend</CardTitle>
            </CardHeader>
            <CardContent className="h-52 px-4 pb-3">
              {performanceData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={performanceData}>
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="score" name="You" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} />
                    <Line type="monotone" dataKey="opponent" name="Opponent" stroke="#ef4444" strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500 text-sm">No matches yet</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Wins Over Time Chart */}
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''} data-testid="wins-over-time-card">
          <CardHeader className="pb-1 pt-3 px-4">
            <CardTitle className={`text-sm font-medium ${isDark ? 'text-white' : ''}`}>Wins Accumulation</CardTitle>
          </CardHeader>
          <CardContent className="h-48 px-4 pb-3">
            {winsChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={winsChartData}>
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="wins" name="Wins" fill="#10b981" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="losses" name="Losses" fill="#ef4444" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500 text-sm">No matches yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Right Column */}
      <div className="w-full lg:w-80 flex flex-col gap-4">
        {/* Today's Matches */}
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''} data-testid="todays-matches">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className={`text-sm font-medium flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <Calendar className="h-4 w-4 text-blue-500" />
              Today's Matches
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-28 overflow-y-auto px-4 pb-3">
            {todaysMatches.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-2">No matches today</p>
            ) : (
              <div className="space-y-1.5">
                {todaysMatches.slice(0, 3).map(match => (
                  <div key={match.id} className={`flex items-center justify-between p-1.5 rounded ${match.result === 'win' ? 'bg-green-50' : 'bg-red-50'}`}>
                    <span className="text-sm font-medium truncate">{match.opponent_name}</span>
                    <span className={`text-sm font-bold ${match.result === 'win' ? 'text-green-600' : 'text-red-600'}`}>
                      {match.user_score}-{match.opponent_score}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Players */}
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''} data-testid="top-players">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className={`text-sm font-medium flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <Trophy className="h-4 w-4 text-yellow-500" />
              Top Players
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            {topPlayers.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-2">No players yet</p>
            ) : (
              <div className="space-y-1.5">
                {topPlayers.map((player, index) => (
                  <div key={player.username} className="flex items-center justify-between" data-testid={`top-player-${index}`}>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold ${index === 0 ? 'text-yellow-500' : index === 1 ? 'text-gray-400' : index === 2 ? 'text-amber-600' : 'text-gray-500'}`}>
                        #{index + 1}
                      </span>
                      <span className={`text-sm font-medium truncate max-w-[140px] ${isDark ? 'text-white' : ''}`}>{player.username}</span>
                    </div>
                    <span className="text-sm font-semibold text-blue-600">{player.win_rate}%</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Results */}
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''} data-testid="recent-results">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className={`text-sm font-medium flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <Flame className="h-4 w-4 text-orange-500" />
              Recent Results
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <div className="flex gap-1 justify-center flex-wrap">
              {matches.slice(0, recentCount).map((match) => (
                <div
                  key={match.id}
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                    match.result === 'win' ? 'bg-green-500' : 'bg-red-500'
                  }`}
                >
                  {match.result === 'win' ? 'W' : 'L'}
                </div>
              ))}
              {matches.length === 0 && <p className="text-sm text-gray-500">No results</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const handleExportPdf = async () => {
    try {
      const response = await axios.get(`${API}/export/stats-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `badminton-stats-${user?.username}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: 'Failed to export PDF', variant: 'destructive' });
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'stats': return renderHomeContent();
      case 'shorts': return <div className="p-4 pb-20 md:pb-4"><ShortsAndPosts /></div>;
      case 'history': return <div className="p-4 pb-20 md:pb-4"><MatchHistory isDark={isDark} /></div>;
      case 'diary': return <div className="p-4 pb-20 md:pb-4"><DiaryPage isDark={isDark} /></div>;
      case 'achievements': return <div className="p-4 pb-20 md:pb-4"><Achievements isDark={isDark} /></div>;
      case 'weekly-report': return <div className="p-4 pb-20 md:pb-4"><WeeklyReport isDark={isDark} /></div>;
      case 'head-to-head': return <div className="p-4 pb-20 md:pb-4"><HeadToHead friends={friendsList} isDark={isDark} /></div>;
      case 'tournament': return <div className="p-4 pb-20 md:pb-4"><TournamentBracket isDark={isDark} /></div>;
      case 'messages': return <div className="p-4 pb-20 md:pb-4"><MessagingPanel /></div>;
      case 'friends': return <div className="p-4 pb-20 md:pb-4"><FriendsList onSelectFriend={(f) => { setViewingUserId(f.id); setActiveSection('public-profile'); }} isDark={isDark} /></div>;
      case 'ai-coach': return <div className="p-4 pb-20 md:pb-4"><AICoach /></div>;
      case 'embed': return <div className="p-4 pb-20 md:pb-4"><EmbedStatCard isDark={isDark} /></div>;
      case 'notifications': return <div className="p-4 pb-20 md:pb-4"><NotificationsPanel isDark={isDark} /></div>;
      case 'profile': return <div className="p-4 pb-20 md:pb-4"><ProfilePage userId={user?.id} isOwnProfile={true} onLogout={handleLogout} isDark={isDark} /></div>;
      case 'public-profile': return <div className="p-4 pb-20 md:pb-4"><ProfilePage userId={viewingUserId} isOwnProfile={false} onBack={() => setActiveSection('friends')} isDark={isDark} /></div>;
      case 'settings': return <div className="p-4 pb-20 md:pb-4"><AccountSettings /></div>;
      case 'addMatch': return (
        <div className="p-4 pb-20 md:pb-4">
          <AddMatchDialog onMatchAdded={() => { handleMatchAdded(); setActiveSection('stats'); }} autoOpen={true} />
        </div>
      );
      default: return renderHomeContent();
    }
  };

  return (
    <div className={`min-h-screen flex ${isDark ? 'bg-slate-900' : 'bg-gray-50'}`} data-testid="dashboard-page">
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex w-20 border-r flex-col items-center py-4 fixed h-full z-10 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
        <div className="mb-6">
          <img
            src="https://customer-assets.emergentagent.com/job_blog-56/artifacts/ar7lcvoq_WhatsApp%20Image%202026-02-21%20at%2022.08.34.jpeg"
            alt="Logo"
            className="h-14 w-14 rounded-lg"
          />
        </div>
        <nav className="flex-1 flex flex-col gap-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveSection(item.id);
                  if (item.id === 'notifications') setUnreadNotifs(0);
                }}
                className={`relative w-14 h-14 rounded-xl flex flex-col items-center justify-center gap-0.5 transition-all ${
                  isActive ? 'bg-blue-600 text-white shadow-lg' : isDark ? 'text-gray-400 hover:bg-slate-700' : 'text-gray-500 hover:bg-gray-100'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <Icon className="h-5 w-5" />
                <span className="text-[9px] font-medium">{item.label}</span>
                {item.badge > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-20 min-h-screen flex flex-col overflow-x-hidden">
        {/* Header */}
        <header className={`sticky top-0 z-10 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} border-b`}>
          <div className="px-4 py-3 flex items-center justify-between">
            <div>
              <h1 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Hi, {user?.username}!
              </h1>
              <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} flex items-center gap-1`}>
                {user?.age_category && <Badge variant="secondary" className="mr-1">{user.age_category}</Badge>}
                Track your victories
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setActiveSection('notifications'); setUnreadNotifs(0); }}
                className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
                data-testid="header-notifications-btn"
              >
                <Bell className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                {unreadNotifs > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {unreadNotifs > 9 ? '9+' : unreadNotifs}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveSection('profile')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                data-testid="header-profile-btn"
              >
                <div className="text-right hidden sm:block">
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user?.username}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>View Profile</p>
                </div>
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center overflow-hidden border-2 border-blue-500"
                  style={{ backgroundColor: user?.banner_color || '#3b82f6' }}
                >
                  {user?.profile_image ? (
                    <img src={user.profile_image} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white font-bold text-lg">{user?.username?.[0]?.toUpperCase()}</span>
                  )}
                </div>
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {renderContent()}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <MobileBottomNav activeSection={activeSection} setActiveSection={setActiveSection} isDark={isDark} />

      {/* Floating Messages Bubble - shown on main pages except messages itself */}
      {activeSection !== 'messages' && activeSection !== 'settings' && activeSection !== 'profile' && activeSection !== 'public-profile' && (
        <button
          onClick={() => setActiveSection('messages')}
          className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-40 w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95"
          data-testid="messages-bubble"
        >
          <MessageSquare className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};

export default DashboardPage;
